import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  ColorSwatch,
  Divider,
  Group,
  Progress,
  Stack,
  Stepper,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBuilding,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconPalette,
  IconPlayerSkipForward,
  IconRocket,
  IconUserPlus,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';

export const Route = createFileRoute('/_app/onboarding')({ component: OnboardingPage });

const STEPS = [
  { label: 'Azienda', description: 'Identità tenant', icon: IconBuilding, eta: '1 min' },
  { label: 'Team', description: 'Primi utenti', icon: IconUserPlus, eta: '30 sec' },
  { label: 'Branding', description: 'Tema & logo', icon: IconPalette, eta: '1 min' },
  { label: 'Dati', description: 'Sample data', icon: IconRocket, eta: '30 sec' },
] as const;

const PALETTE = ['blue', 'teal', 'green', 'grape', 'orange', 'red', 'cyan', 'indigo'] as const;

function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [tenantName, setTenantName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [accent, setAccent] = useState<typeof PALETTE[number]>('blue');
  const [withSample, setWithSample] = useState(true);

  const totalSteps = STEPS.length;
  const progress = useMemo(() => Math.round((step / totalSteps) * 100), [step, totalSteps]);

  const next = () => setStep((s) => Math.min(totalSteps, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    notifications.show({ color: 'green', title: 'Setup completato', message: 'Benvenuto in MaeFwk10!' });
    void navigate({ to: '/' });
  };

  const skipAll = () => {
    notifications.show({ color: 'gray', message: 'Onboarding saltato. Puoi rifarlo dalle Impostazioni.' });
    void navigate({ to: '/' });
  };

  const stepValid = useMemo(() => {
    if (step === 0) return tenantName.trim().length > 0 && adminEmail.trim().length > 0;
    return true;
  }, [step, tenantName, adminEmail]);

  return (
    <Stack maw={760} mx="auto">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={1}>Benvenuto</Title>
          <Text c="dimmed">Configuriamo la tua azienda in pochi passaggi.</Text>
        </div>
        <Group gap={6}>
          <Badge variant="light" leftSection={<IconClock size={12} />}>
            ~3 min totali
          </Badge>
          <Tooltip label="Salta per ora, puoi tornare più tardi">
            <Button
              variant="subtle"
              size="xs"
              color="gray"
              leftSection={<IconPlayerSkipForward size={14} />}
              onClick={skipAll}
            >
              Salta
            </Button>
          </Tooltip>
        </Group>
      </Group>

      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb={6}>
          <Text size="sm" c="dimmed">
            Passo {Math.min(step + 1, totalSteps)} di {totalSteps}
          </Text>
          <Text size="sm" fw={600}>{progress}%</Text>
        </Group>
        <Progress value={progress} size="sm" radius="xl" />
      </Card>

      <Card withBorder padding="xl" radius="md">
        <Stepper active={step} onStepClick={setStep}>
          {STEPS.map((s) => (
            <Stepper.Step
              key={s.label}
              icon={<s.icon size={18} />}
              label={s.label}
              description={
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed">{s.description}</Text>
                  <Badge size="xs" variant="dot" color="gray">{s.eta}</Badge>
                </Group>
              }
            />
          ))}
          <Stepper.Completed>
            <Stack align="center" gap="md" mt="xl">
              <ThemeIcon size={64} radius="xl" variant="light" color="green">
                <IconCheck size={36} />
              </ThemeIcon>
              <Title order={3}>Tutto pronto!</Title>
            </Stack>
          </Stepper.Completed>
        </Stepper>

        <Divider my="lg" />

        {step === 0 && (
          <Stack>
            <TextInput
              label="Nome azienda"
              placeholder="Es. Acme Spa"
              required
              value={tenantName}
              onChange={(e) => setTenantName(e.currentTarget.value)}
              data-autofocus
            />
            <TextInput
              label="Email admin principale"
              type="email"
              required
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.currentTarget.value)}
              placeholder="admin@azienda.it"
            />
          </Stack>
        )}

        {step === 1 && (
          <Stack>
            <Text size="sm" c="dimmed">
              Inviteremo per email i tuoi colleghi al primo accesso. Puoi farlo anche dopo.
            </Text>
            <TextInput
              label="Quante persone usano la piattaforma?"
              placeholder="Es. 25"
              type="number"
              value={teamSize}
              onChange={(e) => setTeamSize(e.currentTarget.value)}
            />
          </Stack>
        )}

        {step === 2 && (
          <Stack>
            <Text size="sm" fw={500}>Colore accento del tenant</Text>
            <Text size="sm" c="dimmed">
              Verrà usato come colore primario dei bottoni e dei link.
            </Text>
            <Group gap="xs">
              {PALETTE.map((c) => (
                <Tooltip key={c} label={c}>
                  <ColorSwatch
                    color={`var(--mantine-color-${c}-6)`}
                    size={36}
                    style={{
                      cursor: 'pointer',
                      outline: accent === c ? '3px solid var(--mantine-color-blue-4)' : undefined,
                      outlineOffset: 2,
                    }}
                    onClick={() => setAccent(c)}
                  />
                </Tooltip>
              ))}
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              💡 Il logo si carica dopo dalla pagina Branding.
            </Text>
          </Stack>
        )}

        {step === 3 && (
          <Stack>
            <Switch
              label="Carica dati di esempio (commesse demo, utenti demo, ecc.)"
              description="Utile per provare l'app. Puoi rimuoverli quando vuoi."
              checked={withSample}
              onChange={(e) => setWithSample(e.currentTarget.checked)}
            />
            <Alert color="blue" variant="light" mt="md">
              <Text size="sm" fw={600} mb={4}>Riepilogo</Text>
              <Stack gap={4}>
                <Group justify="space-between"><Text size="sm">Azienda</Text><Text size="sm" fw={500}>{tenantName || '—'}</Text></Group>
                <Group justify="space-between"><Text size="sm">Admin</Text><Text size="sm" fw={500}>{adminEmail || '—'}</Text></Group>
                <Group justify="space-between"><Text size="sm">Persone</Text><Text size="sm" fw={500}>{teamSize || '—'}</Text></Group>
                <Group justify="space-between">
                  <Text size="sm">Tema</Text>
                  <Group gap={4}>
                    <ColorSwatch color={`var(--mantine-color-${accent}-6)`} size={14} />
                    <Text size="sm" fw={500}>{accent}</Text>
                  </Group>
                </Group>
                <Group justify="space-between"><Text size="sm">Sample data</Text><Text size="sm" fw={500}>{withSample ? 'Sì' : 'No'}</Text></Group>
              </Stack>
            </Alert>
          </Stack>
        )}

        <Group mt="xl" justify="space-between">
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={prev} disabled={step === 0}>
            Indietro
          </Button>
          {step < totalSteps - 1 ? (
            <Button rightSection={<IconChevronRight size={14} />} onClick={next} disabled={!stepValid}>
              Avanti
            </Button>
          ) : (
            <Button rightSection={<IconRocket size={14} />} onClick={finish} color="green">
              Avvia
            </Button>
          )}
        </Group>
      </Card>
    </Stack>
  );
}
